/**
 * Normalise an API error into a string safe to render.
 *
 * `apiClient` rejects with the raw AxiosError, whose `.message` is the generic
 * "Request failed with status code 409". Reading `err.message` therefore throws
 * away the server's actual explanation — which, for state conflicts like
 * "Reopen it first", IS the remedy the user needs.
 *
 * The envelope is not uniform: `error` comes back either as a plain string or
 * as an object `{ message, code }`. Handing the object straight to React
 * renders "[object Object]" (REV-510), and `String(obj)` is no better.
 *
 * REV-797: the UI translates the error CODE, not the server's English message.
 * `error.message` is written for whoever reads the response and is free to be
 * reworded; the code is the stable part (revenant-api's catalog, vendored as
 * @contract/errorCodes). So a failure is shown as, in order:
 *   1. the UI's own text for a specific code (`errors.api.<CODE>`);
 *   2. the caller's fallback, which knows what was being attempted, when the
 *      code is only a generic status ("not found", "invalid") or unknown;
 *   3. client-side failure copy (timeout, unreachable) when nothing arrived;
 *   4. a plain Error's message from our own code (non-axios).
 * The server's message is never returned. `apiErrorMessage` still reads it, for
 * the three auth pages that match phrases in it; see apiError.guard.test.ts.
 */

import { AxiosError, isAxiosError, isCancel } from 'axios';
import { errorCatalog, serviceOnlyErrorCodes } from '@contract/errorCodes';
import i18n from '../i18n';

interface ApiErrorEnvelope {
  error?: string | { message?: string; code?: string; details?: unknown };
  message?: string;
  /**
   * Legacy top-level sibling of `error.code`. Six API sites still emit it
   * (REV-528) precisely so pages reading it directly keep working; the API can
   * drop it once nothing reads it. Kept here as a FALLBACK so this helper is
   * correct against both a pre- and post-REV-528 API, which makes the UI safe
   * to ship in either deploy order.
   */
  code?: string;
}

interface ApiErrorShape {
  response?: {
    status?: number;
    data?: ApiErrorEnvelope;
  };
}

/**
 * The `code` the API attached to the error, when it sent one.
 *
 * Prefers `error.code` (the canonical position since REV-528) and falls back to
 * the legacy top-level `code`. Callers must use this rather than reading either
 * field directly — a page that reads `data.code` itself keeps working right up
 * until the API drops the sibling, then silently stops matching, with no error
 * and no failing test. `apiError.guard.test.ts` enforces that.
 */
export function apiErrorCode(err: unknown): string | undefined {
  const data = (err as ApiErrorShape)?.response?.data;
  if (!data) return undefined;
  if (typeof data.error === 'object' && data.error?.code) return data.error.code;
  if (typeof data.code === 'string' && data.code.trim()) return data.code;
  return undefined;
}

type DetailValue = string | number | boolean;

/**
 * Structured values the API attached to an error (`error.details`, REV-1024),
 * such as `{ requiredRole: 'admin' }` or `{ count: 3 }`.
 *
 * They are values for the UI's own sentence, never a sentence to show. The API
 * only sends flat scalars (and arrays of them); this keeps only that shape too,
 * since it is data from the network.
 */
export type ApiErrorDetails = Record<string, DetailValue | DetailValue[]>;

const isDetailValue = (v: unknown): v is DetailValue =>
  typeof v === 'string' || typeof v === 'boolean' || (typeof v === 'number' && Number.isFinite(v));

export function apiErrorDetails(err: unknown): ApiErrorDetails | undefined {
  const data = (err as ApiErrorShape)?.response?.data;
  const raw = typeof data?.error === 'object' ? data.error?.details : undefined;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
  const out: ApiErrorDetails = {};
  for (const [key, value] of Object.entries(raw)) {
    if (isDetailValue(value)) out[key] = value;
    else if (Array.isArray(value) && value.every(isDetailValue)) out[key] = value;
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

/**
 * The server's own message, or `undefined` when it did not send one.
 *
 * REV-797: for MATCHING only, never for display; use toDisplayError. The
 * message is English, reworded at will, and not translated. Only the auth
 * pages that still match phrases in it may call this (apiError.guard.test.ts).
 */
export function apiErrorMessage(err: unknown): string | undefined {
  const data = (err as ApiErrorShape)?.response?.data;
  if (!data) return undefined;
  if (typeof data.error === 'string' && data.error.trim()) return data.error;
  if (typeof data.error === 'object' && data.error?.message) return data.error.message;
  if (typeof data.message === 'string' && data.message.trim()) return data.message;
  return undefined;
}

/**
 * True when the request was cancelled by the app itself (an AbortController or
 * CancelToken, typically on unmount).
 *
 * REV-979 decision: a cancelled request is not a failure the user needs to hear
 * about. The app asked for it to stop, usually because the screen that wanted
 * the answer is gone. A caller that cancels requests must check this first and
 * set no error at all. toDisplayError cannot express "no message" through its
 * string return, so for a cancellation that slips through it returns the
 * caller's fallback rather than axios's raw "canceled". Nothing in the UI
 * cancels a request today (no AbortController, signal or CancelToken in src/).
 */
export function isRequestCancelled(err: unknown): boolean {
  return isCancel(err);
}

/**
 * Translated copy for a request that failed before any response arrived, or
 * `undefined` when the request got a response (or was not an axios error).
 *
 * The axios 1.x shapes this reads, from its own adapters:
 * - timeout: code ECONNABORTED (xhr adapter, the default
 *   transitional.clarifyTimeoutError=false) or ETIMEDOUT (fetch adapter /
 *   clarifyTimeoutError=true), message "timeout of Nms exceeded".
 * - the browser aborted the xhr (page unload, navigation): ALSO code
 *   ECONNABORTED, message "Request aborted". Not a timeout, so it is told
 *   apart by that message, which axios sets as a literal.
 * - network failure, DNS, CORS, connection reset or refused: ERR_NETWORK,
 *   message "Network Error"; no response either way.
 * - cancellation: ERR_CANCELED (CanceledError); handled by the caller.
 */
function clientFailureMessage(err: unknown): string | undefined {
  if (!isAxiosError(err) || err.response) return undefined;
  if (
    err.code === AxiosError.ETIMEDOUT ||
    (err.code === AxiosError.ECONNABORTED && err.message !== 'Request aborted')
  ) {
    return i18n.t('errors.request.timeout');
  }
  return i18n.t('errors.request.unreachable');
}

/**
 * Codes that only say which HTTP status failed. A caller's fallback ("Failed to
 * load findings") says more than "not found" does, so it wins over these; the
 * code's own text is used only when the caller supplied none. Every other
 * catalogued code names a specific situation and is shown in preference to the
 * fallback.
 */
export const CONTEXTUAL_ERROR_CODES: readonly string[] = [
  'VALIDATION_ERROR',
  'UNAUTHENTICATED',
  'NOT_FOUND',
  'CONFLICT',
  'UNPROCESSABLE',
  'INTERNAL_ERROR',
  'BAD_GATEWAY',
  'ERROR',
];

/**
 * The values a code's text interpolates, from the error's `details`.
 *
 * Names and role slugs are looked up in the UI's own vocabulary so the sentence
 * is in the user's language: `requiredRole` becomes `role` ("Admin"), and the
 * failing `fields` become a list of field names. A value with no entry of ours
 * is used as sent, which for these values is a plain identifier.
 */
function interpolationFor(details: ApiErrorDetails): Record<string, unknown> {
  const vars: Record<string, unknown> = { ...details };
  const role = details.requiredRole;
  if (typeof role === 'string') {
    const key = `organizations.roles.${role}`;
    vars.role = i18n.exists(key) ? i18n.t(key) : role;
  }
  const fields = details.fields;
  if (Array.isArray(fields)) {
    const labels = fields.map((f) => {
      const key = `errors.apiFields.${f}`;
      return i18n.exists(key) ? i18n.t(key) : String(f);
    });
    vars.fields = labels.join(i18n.t('errors.apiListSeparator'));
  }
  return vars;
}

/**
 * The text for a code that came with `details` (REV-1024), or `undefined` when
 * we have none that the details can fill in.
 *
 * In order: a reason-specific text (`errors.apiReason.<CODE>.<reason>`, for a
 * refusal that has several causes), then a text that interpolates the details
 * (`errors.apiDetail.<CODE>`, with `_one`/`_other` plurals when `count` is
 * sent). A text left with an unfilled `{{value}}` is refused: better the
 * generic text than a placeholder on screen.
 */
function textWithDetails(code: string, details: ApiErrorDetails): string | undefined {
  const reason = details.reason;
  if (typeof reason === 'string') {
    const key = `errors.apiReason.${code}.${reason}`;
    if (i18n.exists(key)) return i18n.t(key);
  }
  const key = `errors.apiDetail.${code}`;
  const vars = interpolationFor(details);
  if (!i18n.exists(key, vars)) return undefined;
  const text = i18n.t(key, vars);
  return /{{/.test(text) ? undefined : text;
}

/**
 * The UI's text for an API error code, or `undefined` when the code is not one
 * to show text for: absent, not in the catalog, only ever seen by a machine, a
 * generic status the caller's fallback describes better, or without a string
 * in the active language's bundle or English.
 *
 * `details` can make the text more specific (REV-1024). That text names the
 * actual role, field or count, so it wins over the caller's fallback even for a
 * generic code such as VALIDATION_ERROR.
 */
function textForCode(
  code: string | undefined,
  fallback: string,
  details?: ApiErrorDetails
): string | undefined {
  if (!code || !(code in errorCatalog)) return undefined;
  if (serviceOnlyErrorCodes.includes(code)) return undefined;
  if (details) {
    const specific = textWithDetails(code, details);
    if (specific) return specific;
  }
  if (fallback && CONTEXTUAL_ERROR_CODES.includes(code)) return undefined;
  const key = `errors.api.${code}`;
  return i18n.exists(key) ? i18n.t(key) : undefined;
}

/**
 * The message to show the user for a failed API call.
 *
 * Neither the server's `error.message` nor axios's own `message` is ever
 * returned. The first is untranslated English that may be reworded; the second
 * is "Request failed with status code N", "Network Error" or "timeout of Nms
 * exceeded". Neither is written for a user.
 *
 * @param err - the rejected value from an apiClient call
 * @param fallback - copy to use when the error carries no code worth showing
 */
export function toDisplayError(err: unknown, fallback: string): string {
  if (isRequestCancelled(err)) return fallback;
  const fromCode = textForCode(apiErrorCode(err), fallback, apiErrorDetails(err));
  if (fromCode) return fromCode;
  const clientFailure = clientFailureMessage(err);
  if (clientFailure) return clientFailure;
  if (isAxiosError(err)) return fallback;
  // A genuine Error thrown by our own code (not axios) may carry a message
  // written for the user. Axios's generic status text is still refused here in
  // case something re-wraps an AxiosError into a plain Error.
  if (
    err instanceof Error &&
    err.message &&
    !/^Request failed with status code/.test(err.message)
  ) {
    return err.message;
  }
  return fallback;
}

/**
 * Pull an HTTP status off an axios-style error without assuming the shape.
 *
 * Lives here alongside apiErrorCode/apiErrorMessage so callers that branch on
 * a specific status (409 conflict, 410 gone, …) share one assumption about the
 * error object instead of each re-deriving it.
 */
export function apiErrorStatus(err: unknown): number | undefined {
  return (err as { response?: { status?: number } })?.response?.status;
}

/**
 * Replace a Blob error body with the parsed envelope, in place.
 *
 * A request made with `responseType: 'blob'` — the CSV and report downloads —
 * gets its ERROR body as a Blob too, not as parsed JSON. `apiErrorCode` then
 * finds no envelope on it, so every failure collapses to the caller's fallback
 * and a 429 reads exactly like a 500. Reading the Blob and putting the parsed
 * envelope where the helpers above already look restores the distinction.
 *
 * Await this before `toDisplayError` on any blob-response path. It is a no-op
 * for a normal JSON error, so it is safe on a shared handler.
 *
 * Deliberately leaves the body alone when it is not JSON — an HTML error page
 * from a proxy, or an empty body — so the caller's fallback still applies
 * rather than some fragment of markup reaching the user.
 */
export async function parseBlobErrorBody(err: unknown): Promise<void> {
  const response = (err as { response?: { data?: unknown } } | undefined)?.response;
  if (!response || !(response.data instanceof Blob)) return;
  try {
    const text = await blobToText(response.data);
    if (text) response.data = JSON.parse(text);
  } catch {
    // Not JSON, or unreadable. The Blob stays put and the caller's fallback wins.
  }
}

/**
 * Read a Blob as text via FileReader.
 *
 * FileReader rather than the shorter `blob.text()` on purpose: jsdom's Blob
 * implements neither `text()` nor `arrayBuffer()` (measured — both are
 * `undefined`), so a `text()` call works in a browser and throws in every
 * test. Branching on its presence would be worse: production would take the
 * modern path while the suite only ever covered the fallback. FileReader is
 * implemented in jsdom and in every browser this app supports, so the path the
 * tests exercise is the path that ships.
 *
 * `new Response(blob).text()` is NOT an alternative. Under jsdom it resolves to
 * the literal string "[object Blob]" — it looks like it works and silently
 * returns nonsense.
 */
function blobToText(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error ?? new Error('Could not read the response body'));
    reader.readAsText(blob);
  });
}
